/**
 * Fidelite – Programme de Fidélité
 *
 * This file is the layout orchestrator only.
 * All rendering logic lives in ./components/**
 * All constants live in ./constants.js
 * The API hook lives in ./hooks/useLoyaltyApi.js
 */
import React, { useState, useEffect, useRef } from "react";
import {
  Box, Paper, Button, Avatar, Typography, IconButton, Tooltip, Chip,
  Tabs, Tab, Divider, Snackbar, Alert,
  Menu, MenuItem as MuiMenuItem,
} from "@mui/material";
import { useNavigate } from "react-router-dom";

// Icons used in this layout file
import LoyaltyIcon from "@mui/icons-material/Loyalty";
import StarIcon from "@mui/icons-material/Star";
import CardGiftcardIcon from "@mui/icons-material/CardGiftcard";

import GavelIcon from "@mui/icons-material/Gavel";
import EventNoteIcon from "@mui/icons-material/EventNote";
import GroupWorkIcon from "@mui/icons-material/GroupWork";
import PersonSearchIcon from "@mui/icons-material/PersonSearch";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import AddIcon from "@mui/icons-material/Add";
import RefreshIcon from "@mui/icons-material/Refresh";
import WorkIcon from "@mui/icons-material/Work";
import InventoryIcon from "@mui/icons-material/Inventory";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import SettingsIcon from "@mui/icons-material/Settings";

import { useBrand } from "../../contexts/BrandContext";
import { LOYALTY_API_BASE, SECTION_COLORS } from "./constants";
import { useLoyaltyApi } from "./hooks/useLoyaltyApi";
import { loyaltyFetch } from "./hooks/loyaltyFetch";
import { useSegmentationMode } from "./hooks/useSegmentationMode";

// ── Shared UI ────────────────────────────────────────────────
import SectionHeader from "./components/shared/SectionHeader";

// ── Customer lookup / upsert ───────────────────────────────
import CustomerLookupDialog from "./components/CustomerLookupDialog";
import CustomerUpsertDialog from "./components/CustomerUpsertDialog";
import LoyaltyCustomersListDialog from "./components/LoyaltyCustomersListDialog";

// ── Tiers ────────────────────────────────────────────────────
import TiersTab from "./components/tiers/TiersTab";
import CreateTierDialog from "./components/tiers/CreateTierDialog";
import ViewTierDialog from "./components/tiers/ViewTierDialog";
import EditTierDialog from "./components/tiers/EditTierDialog";
import DeleteTierDialog from "./components/tiers/DeleteTierDialog";

// ── Rewards (mini-orchestrator — dialogs now live inside RewardsTab) ────────
import RewardsTab from "./components/rewards/RewardsTab";



// ── Règles ──────────────────────────────────────────────────
import RulesTab from "./components/rules/RulesTab";
import CreateRuleDialog from "./components/rules/CreateRuleDialog";
import ViewRuleDialog from "./components/rules/ViewRuleDialog";
import EditRuleDialog from "./components/rules/EditRuleDialog";
import DeleteRuleDialog from "./components/rules/DeleteRuleDialog";
import SegmentsTab from "./components/segments/SegmentsTab";
import CreateSegmentDialog from "./components/segments/CreateSegmentDialog";
import ViewSegmentDialog from "./components/segments/ViewSegmentDialog";
import EditSegmentDialog from "./components/segments/EditSegmentDialog";
import DeleteSegmentDialog from "./components/segments/DeleteSegmentDialog";
import ManageSegmentMembersDialog from "./components/segments/ManageSegmentMembersDialog";
import { segmentErrorMessage } from "./components/segments/segmentErrorMessage";

// ── Types de Transactions ──────────────────────────────────────
import TransactionTypesTab from "./components/transactionTypes/TransactionTypesTab";
import CreateTransactionTypeDialog from "./components/transactionTypes/CreateTransactionTypeDialog";
import ViewTransactionTypeDialog from "./components/transactionTypes/ViewTransactionTypeDialog";
import EditTransactionTypeDialog from "./components/transactionTypes/EditTransactionTypeDialog";
import DeleteTransactionTypeDialog from "./components/transactionTypes/DeleteTransactionTypeDialog";

// ── Catalogue Produits ──────────────────────────────────────
import ProductsTab from "./components/products/ProductsTab";

// ── Jobs Internes ────────────────────────────────────────────
import InternalJobsTab from "./components/internalJobs/InternalJobsTab";
import CreateInternalJobDialog from "./components/internalJobs/CreateInternalJobDialog";
import ViewInternalJobDialog from "./components/internalJobs/ViewInternalJobDialog";
import EditInternalJobDialog from "./components/internalJobs/EditInternalJobDialog";
import DeleteInternalJobDialog from "./components/internalJobs/DeleteInternalJobDialog";
import ImportDialog from "./components/ImportDialog";
import LoyaltySettingsPanel from "./components/settings/LoyaltySettingsPanel";

// ─────────────────────────────────────────────────────────────
// Simple table renders for the read-only sections
// (Rules, TransactionTypes — CRUD coming soon)
// ─────────────────────────────────────────────────────────────



// renderRules has been replaced by <RulesTab /> (see components/rules/)

// renderTransactionTypes has been replaced by <TransactionTypesTab /> (see components/transactionTypes/)

// ─────────────────────────────────────────────────────────────
// Main Orchestrator Component
// ─────────────────────────────────────────────────────────────
export default function Fidelite() {
  const navigate = useNavigate();
  const { currentBrand } = useBrand();
  const brandName = currentBrand?.name || "";

  // ── Tab ──────────────────────────────────────────────────
  const [activeTab, setActiveTab] = useState(0);

  // ── Dialog visibility ────────────────────────────────────
  const [searchOpen, setSearchOpen] = useState(false);
  const [customerListOpen, setCustomerListOpen] = useState(false);
  const [upsertOpen, setUpsertOpen] = useState(false);
  const [createTierOpen, setCreateTierOpen] = useState(false);
  const [viewTier, setViewTier] = useState(null);
  const [editTier, setEditTier] = useState(null);
  const [deleteTier, setDeleteTier] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // ── Reward delete state (lifted from RewardsTab) ─────────────
  const [deletingCouponType, setDeletingCouponType] = useState(false);
  const [deletingReward, setDeletingReward] = useState(false);



  // ── Rules dialog visibility ───────────────────────────────
  const [createRuleOpen, setCreateRuleOpen] = useState(false);
  const [viewRule, setViewRule] = useState(null);
  const [editRule, setEditRule] = useState(null);
  const [deleteRule, setDeleteRule] = useState(null);
  const [deletingRule, setDeletingRule] = useState(false);

  // ── TransactionTypes dialog visibility ───────────────────────
  const [createTransactionTypeOpen, setCreateTransactionTypeOpen] = useState(false);
  const [viewTransactionType, setViewTransactionType] = useState(null);
  const [editTransactionType, setEditTransactionType] = useState(null);
  const [deleteTransactionType, setDeleteTransactionType] = useState(null);
  const [deletingTransactionType, setDeletingTransactionType] = useState(false);

  // ── InternalJobs dialog visibility ────────────────────────
  const [createInternalJobOpen, setCreateInternalJobOpen] = useState(false);
  const [viewInternalJob, setViewInternalJob] = useState(null);
  const [editInternalJob, setEditInternalJob] = useState(null);
  const [deleteInternalJob, setDeleteInternalJob] = useState(null);
  const [deletingInternalJob, setDeletingInternalJob] = useState(false);

  // ── Segments dialog visibility ─────────────────────────────
  const [createSegmentOpen, setCreateSegmentOpen] = useState(false);
  const [viewSegment, setViewSegment] = useState(null);
  const [editSegment, setEditSegment] = useState(null);
  const [deleteSegment, setDeleteSegment] = useState(null);
  const [deletingSegment, setDeletingSegment] = useState(false);
  const [manageSegmentMembers, setManageSegmentMembers] = useState(null);

  // ── Import dialog ────────────────────────────────────────────
  const [importOpen, setImportOpen] = useState(false);
  const [importMenuAnchor, setImportMenuAnchor] = useState(null);

  // ── Snackbar ─────────────────────────────────────────────
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "info" });
  const notify = (message, severity = "success") => setSnackbar({ open: true, message, severity });

  // ── Pagination state (one slot per tab) ──────────────────
  const [pages, setPages] = useState([0, 0, 0, 0, 0, 0, 0, 0]);
  const handlePageChange = (tabIdx) => (_, newPage) =>
    setPages((prev) => { const n = [...prev]; n[tabIdx] = newPage; return n; });

  // ── Brand-filtered API hooks ──────────────────────────────
  const brandQuery = brandName ? `?brand=${encodeURIComponent(brandName)}` : "";
  const tiers = useLoyaltyApi(`/admin/loyalty-tiers${brandQuery}`, !!brandName);
  const couponTypes = useLoyaltyApi(`/admin/coupon-types${brandQuery}`, !!brandName);
  const rewards = useLoyaltyApi(`/rewards${brandQuery}`, !!brandName);

  const rules = useLoyaltyApi(`/rules${brandQuery}`, !!brandName);
  const segments = useLoyaltyApi(`/admin/segments${brandQuery}`, !!brandName);
  const transactionTypes = useLoyaltyApi(`/admin/transaction-types${brandQuery}`, !!brandName);
  const internalJobs = useLoyaltyApi(`/admin/internal-jobs${brandQuery}`, !!brandName);
  const { usesUnomi } = useSegmentationMode(brandName);

  // Reset pagination when brand changes
  useEffect(() => {
    setPages([0, 0, 0, 0, 0, 0, 0, 0]);
  }, [brandName]);

  // ── Ensure base tier (min_status_points=0) exists when Tiers tab is active ──
  const ensuredBrandsRef = useRef(new Set());
  useEffect(() => {
    if (activeTab !== 1 || !brandName) return;
    const cacheKey = brandName;
    if (ensuredBrandsRef.current.has(cacheKey)) return;
    ensuredBrandsRef.current.add(cacheKey);
    loyaltyFetch(`${LOYALTY_API_BASE}/admin/loyalty-tiers/ensure-base-tier`, {
      method: "POST",
      headers: { "X-Brand": brandName },
    })
      .then((res) => { if (res.ok) tiers.refetch(); })
      .catch(() => {}); // silent – backend already protects the invariant
  }, [activeTab, brandName]); // eslint-disable-line react-hooks/exhaustive-deps



  const tabLoading = [false, tiers.loading, false, rewards.loading || couponTypes.loading, transactionTypes.loading, rules.loading, segments.loading, internalJobs.loading];

  const handleRefreshAll = () => {
    tiers.refetch(); rewards.refetch(); couponTypes.refetch();
    rules.refetch(); segments.refetch(); transactionTypes.refetch(); internalJobs.refetch();
    notify("Données rafraîchies");
  };

  // ProductsTab manages its own API calls internally — no extra refetch needed here

  const settingsColor = SECTION_COLORS.tiers;

  // ── InternalJobs CRUD handlers ─────────────────────────────
  const handleDeleteInternalJobConfirmed = async () => {
    if (!deleteInternalJob) return;
    setDeletingInternalJob(true);
    try {
      const res = await loyaltyFetch(`${LOYALTY_API_BASE}/admin/internal-jobs/${deleteInternalJob.id}`, {
        method: "DELETE",
        headers: { "X-Brand": brandName }
      });
      if (!res.ok) { const e = await res.json().catch(() => null); throw new Error(e?.detail || `Erreur HTTP ${res.status}`); }
      setDeleteInternalJob(null);
      internalJobs.refetch();
      notify(`Job "${deleteInternalJob.job_key}" supprimé avec succès`);
    } catch (err) { notify(err.message, "error"); } finally { setDeletingInternalJob(false); }
  };

  const handleRunInternalJob = async (job) => {
    try {
      const res = await loyaltyFetch(`${LOYALTY_API_BASE}/admin/internal-jobs/${job.id}/run`, {
        method: "POST",
        headers: { "X-Brand": brandName }
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.detail || `Erreur HTTP ${res.status}`);
      internalJobs.refetch();
      notify(`Job "${job.job_key}" exécuté — ${data.created} transaction(s) créée(s)`, "success");
    } catch (err) { notify(err.message, "error"); }
  };

  // ── Segments CRUD handlers ────────────────────────────────
  const handleDeleteSegmentConfirmed = async () => {
    if (!deleteSegment) return;
    setDeletingSegment(true);
    try {
      const res = await loyaltyFetch(`${LOYALTY_API_BASE}/admin/segments/${deleteSegment.id}`, {
        method: "DELETE",
        headers: { "X-Brand": brandName }
      });
      if (!res.ok) {
        const e = await res.json().catch(() => null);
        throw new Error(segmentErrorMessage(res.status, e?.detail, "Suppression segment"));
      }
      setDeleteSegment(null);
      segments.refetch();
      notify(`Segment "${deleteSegment.name}" supprimé avec succès`);
    } catch (err) { notify(err.message, "error"); } finally { setDeletingSegment(false); }
  };

  const handleRecomputeAllSegments = async () => {
    try {
      const res = await loyaltyFetch(`${LOYALTY_API_BASE}/admin/segments/recompute`, {
        method: "POST",
        headers: { "X-Brand": brandName },
      });
      const data = await res.json().catch(() => null);
      if (!res.ok) throw new Error(segmentErrorMessage(res.status, data?.detail, "Recompute segments"));
      segments.refetch();
      notify("Segments dynamiques recalculés (moteur fidélité)");
    } catch (err) {
      notify(err.message, "error");
    }
  };

  const handleRecomputeSegment = async (segment) => {
    if (!segment?.id) return;
    try {
      const res = await loyaltyFetch(
        `${LOYALTY_API_BASE}/admin/segments/${segment.id}/recompute`,
        { method: "POST", headers: { "X-Brand": brandName } },
      );
      const data = await res.json().catch(() => null);
      if (!res.ok) throw new Error(segmentErrorMessage(res.status, data?.detail, "Recalcul segment"));
      segments.refetch();
      notify(`Segment « ${segment.name} » recalculé`);
    } catch (err) {
      notify(err.message, "error");
    }
  };

  // ── Tier CRUD handlers ────────────────────────────────────
  const handleDeleteTierConfirmed = async () => {
    if (!deleteTier) return;
    setDeleting(true);
    try {
      const res = await loyaltyFetch(`${LOYALTY_API_BASE}/admin/loyalty-tiers/${deleteTier.id}`, {
        method: "DELETE",
        headers: { "X-Brand": brandName }
      });
      if (!res.ok) { const e = await res.json().catch(() => null); throw new Error(e?.detail || `Erreur HTTP ${res.status}`); }
      setDeleteTier(null);
      tiers.refetch();
      notify(`Niveau "${deleteTier.name}" supprimé avec succès`);
    } catch (err) { notify(err.message, "error"); } finally { setDeleting(false); }
  };

  // ── CouponType CRUD handlers (lifted from RewardsTab) ────────
  const handleDeleteCouponTypeConfirmed = async (couponType, setDeleteCouponType) => {
    if (!couponType) return;
    setDeletingCouponType(true);
    try {
      const res = await loyaltyFetch(`${LOYALTY_API_BASE}/admin/coupon-types/${couponType.id}`, {
        method: "DELETE", headers: { "X-Brand": brandName },
      });
      if (!res.ok) { const e = await res.json().catch(() => null); throw new Error(e?.detail || `Erreur HTTP ${res.status}`); }
      setDeleteCouponType(null);
      couponTypes.refetch();
      notify(`Type de coupon "${couponType.name}" supprimé avec succès`);
    } catch (err) { notify(err.message, "error"); } finally { setDeletingCouponType(false); }
  };

  // ── Reward delete handler (called from RewardsTab) ────────────
  const handleDeleteRewardConfirmed = async (reward, setDeleteReward) => {
    if (!reward) return;
    setDeletingReward(true);
    try {
      const res = await loyaltyFetch(`${LOYALTY_API_BASE}/rewards/${reward.id}`, {
        method: "DELETE", headers: { "X-Brand": brandName },
      });
      if (!res.ok) { const e = await res.json().catch(() => null); throw new Error(e?.detail || `Erreur HTTP ${res.status}`); }
      setDeleteReward(null);
      rewards.refetch();
      notify(`Récompense "${reward.name}" supprimée avec succès`);
    } catch (err) { notify(err.message, "error"); } finally { setDeletingReward(false); }
  };



  // ── Rules CRUD handlers ───────────────────────────────────
  const handleDeleteRuleConfirmed = async () => {
    if (!deleteRule) return;
    setDeletingRule(true);
    try {
      const res = await loyaltyFetch(`${LOYALTY_API_BASE}/rules/${deleteRule.id}`, {
        method: "DELETE",
        headers: { "X-Brand": brandName }
      });
      if (!res.ok) { const e = await res.json().catch(() => null); throw new Error(e?.detail || `Erreur HTTP ${res.status}`); }
      setDeleteRule(null);
      rules.refetch();
      notify(`Règle "${deleteRule.transaction_type}" supprimée avec succès`);
    } catch (err) { notify(err.message, "error"); } finally { setDeletingRule(false); }
  };

  const handleReorderRules = async (orderedRuleIds) => {
    if (!Array.isArray(orderedRuleIds) || orderedRuleIds.length === 0) return;
    const res = await loyaltyFetch(`${LOYALTY_API_BASE}/rules/reorder`, {
      method: "POST",
      headers: { "Content-Type": "application/json", "X-Brand": brandName },
      body: JSON.stringify({ rule_ids: orderedRuleIds }),
    });
    if (!res.ok) {
      const e = await res.json().catch(() => null);
      throw new Error(e?.detail || `Erreur HTTP ${res.status}`);
    }
    rules.refetch();
    notify("Priorités des règles mises à jour");
  };

  // ── Tab configuration ─────────────────────────────────────
  const tabItems = [
    { label: "Configuration", icon: <SettingsIcon sx={{ fontSize: 16 }} /> },
    { label: "Niveaux de Fidélité", icon: <StarIcon sx={{ fontSize: 16 }} /> },
    { label: "Catalogue Produits", icon: <InventoryIcon sx={{ fontSize: 16 }} /> },
    { label: "Récompenses", icon: <CardGiftcardIcon sx={{ fontSize: 16 }} /> },
    { label: "Types de Transactions", icon: <EventNoteIcon sx={{ fontSize: 16 }} /> },
    { label: "Règles", icon: <GavelIcon sx={{ fontSize: 16 }} /> },
    { label: "Segments", icon: <GroupWorkIcon sx={{ fontSize: 16 }} /> },
    { label: "Jobs Internes", icon: <WorkIcon sx={{ fontSize: 16 }} /> },
  ];
  const tabColors = [
    settingsColor, SECTION_COLORS.tiers, SECTION_COLORS.rewards,
    SECTION_COLORS.rewards, SECTION_COLORS.transactionTypes, SECTION_COLORS.rules, SECTION_COLORS.segments, SECTION_COLORS.internalJobs,
  ];
  const tabRefresh = [
    null,
    tiers.refetch,
    null, // ProductsTab manages its own refetch internally
    () => { rewards.refetch(); couponTypes.refetch(); },
    transactionTypes.refetch,
    rules.refetch,
    segments.refetch,
    internalJobs.refetch,
  ];

  // ── Tab content ───────────────────────────────────────────
  const tabContent = [
    <LoyaltySettingsPanel brandName={brandName} onNotify={notify} />,
    <TiersTab
      tiersApi={tiers} pages={pages} handlePageChange={handlePageChange}
      onView={setViewTier} onEdit={setEditTier} onDelete={setDeleteTier}
    />,
    <ProductsTab notify={notify} />,
    <RewardsTab
      couponTypesApi={couponTypes}
      rewardsApi={rewards}
      onDeleteCouponTypeConfirmed={handleDeleteCouponTypeConfirmed}
      deletingCouponType={deletingCouponType}
      onDeleteRewardConfirmed={handleDeleteRewardConfirmed}
      deletingReward={deletingReward}
      notify={notify}
    />,
    <TransactionTypesTab
      transactionTypesApi={transactionTypes} pages={pages} handlePageChange={handlePageChange}
      onView={setViewTransactionType} onEdit={setEditTransactionType} onDelete={setDeleteTransactionType}
    />,
    <RulesTab
      rulesApi={rules} pages={pages} handlePageChange={handlePageChange}
      onView={setViewRule} onEdit={setEditRule} onDelete={setDeleteRule}
      onReorder={handleReorderRules}
    />,
    <SegmentsTab
      segmentsApi={segments} pages={pages} handlePageChange={handlePageChange}
      onView={setViewSegment} onEdit={setEditSegment} onDelete={setDeleteSegment}
      onViewMembers={setManageSegmentMembers}
      onRecomputeSegment={handleRecomputeSegment}
      tabIndex={6}
      usesUnomi={usesUnomi}
    />,
    <InternalJobsTab
      internalJobsApi={internalJobs} pages={pages} handlePageChange={handlePageChange}
      onView={setViewInternalJob} onEdit={setEditInternalJob} onDelete={setDeleteInternalJob}
      onRun={handleRunInternalJob} tabIndex={7}
    />,
  ];

  return (
    <>
      {/* ── Dialogs ────────────────────────────────────────── */}
      <CustomerLookupDialog open={searchOpen} onClose={() => setSearchOpen(false)} />
      <LoyaltyCustomersListDialog open={customerListOpen} onClose={() => setCustomerListOpen(false)} brandName={brandName} />
      <CustomerUpsertDialog open={upsertOpen} onClose={() => setUpsertOpen(false)} onSaved={(msg) => notify(msg, "success")} />

      {/* Tiers */}
      <CreateTierDialog
        open={createTierOpen}
        onClose={() => setCreateTierOpen(false)}
        onCreated={() => { tiers.refetch(); notify("Niveau de fidélité créé avec succès"); }}
      />
      <ViewTierDialog
        open={Boolean(viewTier)} tier={viewTier}
        onClose={() => setViewTier(null)} onEditRequest={setEditTier}
      />
      <EditTierDialog
        open={Boolean(editTier)} tier={editTier}
        onClose={() => setEditTier(null)}
        onSaved={() => { tiers.refetch(); notify("Niveau mis à jour avec succès"); }}
      />
      <DeleteTierDialog
        open={Boolean(deleteTier)} tier={deleteTier}
        onClose={() => !deleting && setDeleteTier(null)}
        onConfirm={handleDeleteTierConfirmed} deleting={deleting}
      />

      {/* Rewards dialogs are now managed inside <RewardsTab /> */}



      {/* Règles */}
      <CreateRuleDialog
        open={createRuleOpen}
        onClose={() => setCreateRuleOpen(false)}
        onCreated={() => { rules.refetch(); notify("Règle créée avec succès"); }}
      />
      <ViewRuleDialog
        open={Boolean(viewRule)} rule={viewRule}
        onClose={() => setViewRule(null)} onEditRequest={setEditRule}
      />
      <EditRuleDialog
        open={Boolean(editRule)} rule={editRule}
        onClose={() => setEditRule(null)}
        onSaved={() => { rules.refetch(); notify("Règle mise à jour avec succès"); }}
      />
      <DeleteRuleDialog
        open={Boolean(deleteRule)} rule={deleteRule}
        onClose={() => !deletingRule && setDeleteRule(null)}
        onConfirm={handleDeleteRuleConfirmed} deleting={deletingRule}
      />

      {/* Segments */}
      <CreateSegmentDialog
        open={createSegmentOpen}
        onClose={() => setCreateSegmentOpen(false)}
        onCreated={() => { segments.refetch(); notify("Segment créé avec succès"); }}
      />
      <ViewSegmentDialog
        open={Boolean(viewSegment)} segment={viewSegment}
        onClose={() => setViewSegment(null)} onEditRequest={setEditSegment}
        onViewMembers={setManageSegmentMembers}
      />
      <ManageSegmentMembersDialog
        open={Boolean(manageSegmentMembers)} segment={manageSegmentMembers}
        brandName={brandName}
        usesUnomi={usesUnomi}
        onChanged={() => segments.refetch()}
        onClose={() => setManageSegmentMembers(null)}
      />
      <EditSegmentDialog
        open={Boolean(editSegment)} segment={editSegment}
        onClose={() => setEditSegment(null)}
        onSaved={() => { segments.refetch(); notify("Segment mis à jour avec succès"); }}
      />
      <DeleteSegmentDialog
        open={Boolean(deleteSegment)} segment={deleteSegment}
        onClose={() => !deletingSegment && setDeleteSegment(null)}
        onConfirm={handleDeleteSegmentConfirmed} deleting={deletingSegment}
      />

      {/* Types de Transactions */}
      <CreateTransactionTypeDialog
        open={createTransactionTypeOpen}
        onClose={() => setCreateTransactionTypeOpen(false)}
        onCreated={() => { transactionTypes.refetch(); notify("Type de transaction créé avec succès"); }}
      />
      <ViewTransactionTypeDialog
        open={Boolean(viewTransactionType)} transactionType={viewTransactionType}
        onClose={() => setViewTransactionType(null)} onEditRequest={setEditTransactionType}
      />
      <EditTransactionTypeDialog
        open={Boolean(editTransactionType)} transactionType={editTransactionType}
        onClose={() => setEditTransactionType(null)}
        onSaved={() => { transactionTypes.refetch(); notify("Type de transaction mis à jour avec succès"); }}
      />
      <DeleteTransactionTypeDialog
        open={Boolean(deleteTransactionType)} transactionType={deleteTransactionType}
        onClose={() => !deletingTransactionType && setDeleteTransactionType(null)}
        onConfirm={async () => {
          if (!deleteTransactionType) return;
          setDeletingTransactionType(true);
          try {
            const res = await loyaltyFetch(`${LOYALTY_API_BASE}/admin/transaction-types/${deleteTransactionType.id}`, {
              method: "DELETE",
              headers: { "X-Brand": brandName }
            });
            if (!res.ok) { const e = await res.json().catch(() => null); throw new Error(e?.detail || `Erreur HTTP ${res.status}`); }
            setDeleteTransactionType(null);
            transactionTypes.refetch();
            notify(`Type "${deleteTransactionType.name}" supprimé avec succès`);
          } catch (err) { notify(err.message, "error"); } finally { setDeletingTransactionType(false); }
        }}
        deleting={deletingTransactionType}
      />

      {/* Import Dialog */}
      <ImportDialog
        open={importOpen}
        onClose={() => setImportOpen(false)}
        brandName={brandName}
        onImported={(mode, data) => {
          const label = mode === "customers" ? "clients" : "transactions";
          const count = mode === "customers" ? data.upserted : data.succeeded;
          notify(`Import ${label} : ${count} enregistrement(s) traité(s) avec succès`, "success");
        }}
      />

      {/* Jobs Internes */}
      <CreateInternalJobDialog
        open={createInternalJobOpen}
        onClose={() => setCreateInternalJobOpen(false)}
        onCreated={() => { internalJobs.refetch(); notify("Job interne créé avec succès"); }}
      />
      <ViewInternalJobDialog
        open={Boolean(viewInternalJob)} job={viewInternalJob}
        onClose={() => setViewInternalJob(null)} onEditRequest={setEditInternalJob}
        onRunSuccess={() => internalJobs.refetch()}
      />
      <EditInternalJobDialog
        open={Boolean(editInternalJob)} job={editInternalJob}
        onClose={() => setEditInternalJob(null)}
        onSaved={() => { internalJobs.refetch(); notify("Job mis à jour avec succès"); }}
      />
      <DeleteInternalJobDialog
        open={Boolean(deleteInternalJob)} job={deleteInternalJob}
        onClose={() => !deletingInternalJob && setDeleteInternalJob(null)}
        onConfirm={handleDeleteInternalJobConfirmed} deleting={deletingInternalJob}
      />

      {/* Snackbar */}
      <Snackbar open={snackbar.open} autoHideDuration={3000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}>
        <Alert onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity={snackbar.severity}
          sx={{ borderRadius: 2, fontFamily: "Montserrat, sans-serif" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* ── Page Layout ──────────────────────────────────────── */}
      <Box
        bgcolor="#f5f7fa"
        minHeight="100vh"
        p={{ xs: 1, md: 2 }}
        sx={{ display: "flex", flexDirection: "column" }}
      >
        <Box
          mx="auto"
          sx={{
            width: "100%",
            maxWidth: { xs: "100%", lg: 1440, xl: 1680 },
            flex: 1,
            display: "flex",
            flexDirection: "column",
          }}
        >

          {/* Header */}
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={3} flexWrap="wrap" gap={2}>
            <Box display="flex" alignItems="center" gap={1.5}>
              <Avatar sx={{ width: 44, height: 44, background: "linear-gradient(135deg, #3cb0d6 0%, #2a9db4 100%)", boxShadow: "0 4px 16px rgba(60, 176, 214, 0.3)" }}>
                <LoyaltyIcon sx={{ fontSize: 24 }} />
              </Avatar>
              <Box>
                <Typography variant="h5" fontWeight={800} fontFamily="Montserrat, sans-serif"
                  sx={{ background: "linear-gradient(135deg, #1a1a2e 0%, #3cb0d6 100%)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
                  Programme de Fidélité
                </Typography>
                <Typography fontSize={12} color="text.secondary" fontFamily="Montserrat, sans-serif">
                  Gestion centralisée du moteur de fidélité
                </Typography>
              </Box>
            </Box>
            <Box display="flex" gap={1} flexWrap="wrap" alignItems="center">
              {/* Import button */}
              <Button
                variant="outlined"
                startIcon={<FileUploadIcon />}
                onClick={() => setImportOpen(true)}
                sx={{
                  borderRadius: 2, textTransform: "none", fontWeight: 700,
                  fontFamily: "Montserrat, sans-serif",
                  borderColor: "#3cb0d6", color: "#3cb0d6",
                  "&:hover": { borderColor: "#2a9db4", bgcolor: "#ecf9fd" }
                }}
              >
                Importer
              </Button>
              {/* <Button variant="outlined" onClick={() => setUpsertOpen(true)} startIcon={<PersonAddIcon />}
                sx={{
                  borderRadius: 2, textTransform: "none", fontWeight: 600, fontFamily: "Montserrat, sans-serif",
                  borderColor: SECTION_COLORS.search.main, color: SECTION_COLORS.search.main,
                  "&:hover": { borderColor: SECTION_COLORS.search.main, bgcolor: SECTION_COLORS.search.light }
                }}>
                Ajouter un Client
              </Button> */}
              <Button variant="outlined" onClick={() => setSearchOpen(true)} startIcon={<PersonSearchIcon />}
                sx={{
                  borderRadius: 2, textTransform: "none", fontWeight: 600, fontFamily: "Montserrat, sans-serif",
                  borderColor: SECTION_COLORS.search.main, color: SECTION_COLORS.search.main,
                  "&:hover": { borderColor: SECTION_COLORS.search.main, bgcolor: SECTION_COLORS.search.light }
                }}>
                Consulter un Client
              </Button>
              <Tooltip title="Voir la liste des clients fidélité" placement="top" arrow>
                <IconButton
                  onClick={() => setCustomerListOpen(true)}
                  sx={{
                    width: 44, height: 44, borderRadius: 2,
                    color: SECTION_COLORS.search.main,
                    border: `1px solid ${SECTION_COLORS.search.main}55`,
                    background: "#fff",
                    "&:hover": { bgcolor: SECTION_COLORS.search.light }
                  }}
                >
                  <FormatListBulletedIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Rafraîchir tout" placement="top" arrow>
                <IconButton onClick={handleRefreshAll}
                  sx={{
                    width: 44, height: 44, borderRadius: 2,
                    color: "#fff",
                    background: "linear-gradient(135deg, #3cb0d6 0%, #2a9db4 100%)",
                    boxShadow: "0 4px 12px rgba(60, 176, 214, 0.25)",
                    "&:hover": { boxShadow: "0 6px 20px rgba(60, 176, 214, 0.35)" }
                  }}>
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>



          {/* Tabbed Content */}
          <Paper
            elevation={0}
            sx={{
              borderRadius: 4,
              border: "1px solid #e8ecf1",
              overflow: "hidden",
              background: "#fff",
              boxShadow: "0 1px 8px rgba(0,0,0,0.04)",
              flex: 1,
              display: "flex",
              flexDirection: "column",
              minHeight: 420,
              height: { xs: "auto", md: "calc(100vh - 170px)" },
            }}
          >
            {/* Tab bar */}
            <Box
              sx={{
                borderBottom: "2px solid #f1f3f5",
                background: "linear-gradient(180deg, #fafbfd 0%, #fff 100%)",
                position: "sticky",
                top: 0,
                zIndex: 3,
              }}
            >
              <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)}
                variant="scrollable" scrollButtons="auto"
                sx={{
                  px: 2,
                  "& .MuiTab-root": { textTransform: "none", fontWeight: 600, fontFamily: "Montserrat, sans-serif", fontSize: 13, minHeight: 52, color: "#64748b", transition: "all 0.2s", "&:hover": { color: "#1a1a2e" } },
                  "& .Mui-selected": { color: `${tabColors[activeTab]?.main} !important` },
                  "& .MuiTabs-indicator": { height: 3, borderRadius: "3px 3px 0 0", background: tabColors[activeTab]?.gradient },
                }}>
                {tabItems.map((tab, idx) => (
                  <Tab key={idx} label={
                    <Box display="flex" alignItems="center" gap={0.75}>
                      {tab.icon}
                      {tab.label}
                    </Box>
                  } />
                ))}
              </Tabs>
            </Box>

            {/* Tab panel */}
            <Box
              p={{ xs: 2, md: 2.5 }}
              sx={{
                flex: 1,
                minHeight: 300,
                overflow: "auto",
              }}
            >
              <Box
                sx={{
                  position: "sticky",
                  top: 0,
                  zIndex: 2,
                  background: "#fff",
                  pt: 0.5,
                }}
              >
                <SectionHeader
                  icon={tabItems[activeTab].icon}
                  title={tabItems[activeTab].label}
                  color={tabColors[activeTab]}
                  onRefresh={tabRefresh[activeTab]}
                  refreshing={tabLoading[activeTab]}
                >
                  {/* "Ajouter" buttons injected per-tab — Rewards tab manages its own Add buttons internally */}

                  {activeTab === 1 && (
                  <Button size="small" variant="contained" startIcon={<AddIcon />}
                    onClick={() => setCreateTierOpen(true)}
                    sx={{
                      textTransform: "none", fontWeight: 600, fontFamily: "Montserrat, sans-serif",
                      fontSize: 12, borderRadius: 2, background: SECTION_COLORS.tiers.gradient,
                      boxShadow: `0 2px 8px ${SECTION_COLORS.tiers.main}30`,
                      "&:hover": { boxShadow: `0 4px 14px ${SECTION_COLORS.tiers.main}40` }
                    }}>
                    Ajouter un Niveau
                  </Button>
                )}
                {/* Tab 2 = Catalogue Produits — Add buttons managed inside ProductsTab */}
                {activeTab === 4 && (
                  <Button size="small" variant="contained" startIcon={<AddIcon />}
                    onClick={() => setCreateTransactionTypeOpen(true)}
                    sx={{
                      textTransform: "none", fontWeight: 600, fontFamily: "Montserrat, sans-serif",
                      fontSize: 12, borderRadius: 2, background: SECTION_COLORS.transactionTypes.gradient,
                      boxShadow: `0 2px 8px ${SECTION_COLORS.transactionTypes.main}30`,
                      "&:hover": { boxShadow: `0 4px 14px ${SECTION_COLORS.transactionTypes.main}40` }
                    }}>
                    Ajouter un Type de Transaction
                  </Button>
                )}
                {activeTab === 5 && (
                  <Button size="small" variant="contained" startIcon={<AddIcon />}
                    onClick={() => setCreateRuleOpen(true)}
                    sx={{
                      textTransform: "none", fontWeight: 600, fontFamily: "Montserrat, sans-serif",
                      fontSize: 12, borderRadius: 2, background: SECTION_COLORS.rules.gradient,
                      boxShadow: `0 2px 8px ${SECTION_COLORS.rules.main}30`,
                      "&:hover": { boxShadow: `0 4px 14px ${SECTION_COLORS.rules.main}40` }
                    }}>
                    Ajouter une Règle
                  </Button>
                )}
                {activeTab === 6 && (
                  <>
                    {usesUnomi && (
                      <Chip
                        label="Segmentation Unomi"
                        size="small"
                        sx={{ mr: 1, fontWeight: 700, bgcolor: "#fdf4ff", color: "#a21caf" }}
                      />
                    )}
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={handleRecomputeAllSegments}
                      sx={{ mr: 1, textTransform: "none", fontWeight: 600, borderRadius: 2 }}
                    >
                      Recalculer segments dynamiques
                    </Button>
                    <Button size="small" variant="contained" startIcon={<AddIcon />}
                      onClick={() => setCreateSegmentOpen(true)}
                      sx={{
                        textTransform: "none", fontWeight: 600, fontFamily: "Montserrat, sans-serif",
                        fontSize: 12, borderRadius: 2, background: SECTION_COLORS.segments.gradient,
                        boxShadow: `0 2px 8px ${SECTION_COLORS.segments.main}30`,
                        "&:hover": { boxShadow: `0 4px 14px ${SECTION_COLORS.segments.main}40` }
                      }}>
                      Ajouter un Segment
                    </Button>
                  </>
                )}
                {activeTab === 7 && (
                  <Button size="small" variant="contained" startIcon={<AddIcon />}
                    onClick={() => setCreateInternalJobOpen(true)}
                    sx={{
                      textTransform: "none", fontWeight: 600, fontFamily: "Montserrat, sans-serif",
                      fontSize: 12, borderRadius: 2, background: SECTION_COLORS.internalJobs.gradient,
                      boxShadow: `0 2px 8px ${SECTION_COLORS.internalJobs.main}30`,
                      "&:hover": { boxShadow: `0 4px 14px ${SECTION_COLORS.internalJobs.main}40` }
                    }}>
                    Ajouter un Job Interne
                  </Button>
                )}
                </SectionHeader>

                <Divider sx={{ mb: 2 }} />
              </Box>

              {tabContent[activeTab]}
            </Box>
          </Paper>
        </Box>
      </Box>
    </>
  );
}
